import { useMutation } from "@apollo/client";
import type {
  UpdateAgentInput as UpdateAgentInputType,
  UpdateAgentPayload,
  AgentPatch,
} from "@casey/db-types";
import {
  UPDATE_CUSTOM_TEMPLATE,
  GET_TEMPLATE_BY_ID,
  GET_ORGANIZATION_AGENTS,
} from "../gql";

// Convenience type for the hook's API - matches AgentPatch but with typed sections
type UpdateAgentInput = {
  id: string;
} & Pick<
  AgentPatch,
  "name" | "description" | "goal" | "prompt" | "greetingTitle" | "greetingBody"
> & {
    sections?: any; // JSONB array (will be stringified)
    config?: any; // JSONB object (will be stringified)
    flowMeta?: any; // JSONB object for flow editor metadata (will be stringified)
  };

interface UpdateAgentVariables {
  input: UpdateAgentInputType;
}

interface UpdateAgentResponse {
  updateAgent: UpdateAgentPayload;
}

export function useUpdateAgent() {
  const [updateAgentMutation, { data, loading, error }] = useMutation<
    UpdateAgentResponse,
    UpdateAgentVariables
  >(UPDATE_CUSTOM_TEMPLATE, {
    refetchQueries: (result) => {
      const organizationId = result.data?.updateAgent?.agent?.organizationId;
      if (organizationId) {
        return [
          { query: GET_ORGANIZATION_AGENTS, variables: { organizationId } },
        ];
      }
      return [];
    },
  });

  const updateAgent = async (agentData: UpdateAgentInput) => {
    try {
      const agentPatch: AgentPatch = {
        name: agentData.name,
        description: agentData.description,
        greetingTitle: agentData.greetingTitle,
        greetingBody: agentData.greetingBody,
        goal: agentData.goal,
        sections: agentData.sections
          ? JSON.stringify(agentData.sections)
          : undefined,
        config: agentData.config ? JSON.stringify(agentData.config) : undefined,
        flowMeta: agentData.flowMeta
          ? JSON.stringify(agentData.flowMeta)
          : undefined,
        prompt: agentData.prompt,
      };

      const result = await updateAgentMutation({
        variables: {
          input: {
            id: agentData.id,
            patch: agentPatch,
          },
        },
      });

      const updatedAgent = result.data?.updateAgent?.agent;
      return updatedAgent;
    } catch (err) {
      console.error("Error updating agent:", err);
      throw err;
    }
  };

  return {
    updateAgent,
    data: data?.updateAgent?.agent,
    loading,
    error,
  };
}

export type { UpdateAgentInput };

// Backwards compatibility
export { useUpdateAgent as useUpdateTemplate };
export type { UpdateAgentInput as UpdateTemplateInput };
